// <Banner> — persistent banner; never auto-dismiss for critical.
//
// Per anti-pattern A17 (boss §13): errors must use Banner, not Toast.
// Per a11y-plan.md §0.1 #6 + _common-patterns.md CP-4.2: first occurrence
// per (key, session) → role="alert" + aria-live="assertive"; subsequent →
// role="status" + aria-live="polite". The flip is done via useStateNarration.
//
// Tokens consumed: color.semantic.{info,warning,danger}, radius.md,
// space[2..4], font.role.body, color.surface[2], color.outline.

import { type ReactNode } from 'react';
import { tokens } from '../tokens';
import { useStateNarration } from '../hooks';
import { Icon, type IconName } from './Icon';

export type BannerSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface BannerAction {
  label: string;
  onClick(): void;
}

export interface BannerProps {
  severity: BannerSeverity;
  title: string;
  body?: ReactNode;
  /** Stable per-session key for the alert→status flip. */
  narrationKey?: string;
  /** When true, banner stays mounted; default true. Critical is always persistent. */
  persistent?: boolean;
  /** Error-catalog code; surfaced as a Roboto-Mono identifier; consumed by audit. */
  errorCode?: string;
  /** Recovery action (≤ 1 primary CTA per row per playbook). */
  actions?: BannerAction[];
  onDismiss?: () => void;
  className?: string;
  'data-testid'?: string;
  /**
   * Optional override for the leading glyph. Defaults to the
   * severity-driven icon (`info` / `warning` / `error` /
   * `priority_high`). Used by domain-specific banners where the
   * `ICON_SYSTEM_PLAYBOOK.md` mandates a more precise glyph — e.g.
   * `sync_problem` for stale data, `cloud_off` for offline, `lock`
   * for permission-denied (WP-8 sensor / live overlay card).
   */
  icon?: IconName;
}

const SEVERITY_ICON: Record<BannerSeverity, IconName> = {
  info: 'info',
  warning: 'warning',
  error: 'error',
  critical: 'priority_high',
};

const SEVERITY_COLOR: Record<BannerSeverity, string> = {
  info: tokens.color.semantic.info,
  warning: tokens.color.semantic.warning,
  error: tokens.color.semantic.danger,
  critical: tokens.color.semantic.danger,
};

export function Banner({
  severity,
  title,
  body,
  narrationKey,
  persistent = true,
  errorCode,
  actions,
  onDismiss,
  className,
  'data-testid': testId,
  icon,
}: BannerProps) {
  // Compute role / aria-live via the narration hook. When narrationKey is
  // omitted, default to status/polite (downstream: per-message dynamic text
  // is what wins assertive treatment).
  const narration = useStateNarration({
    key: narrationKey ?? `banner:${severity}:${title}`,
    severity,
    text: typeof body === 'string' ? body : title,
  });

  const isCritical = severity === 'critical';
  const dismissible = !isCritical && persistent ? Boolean(onDismiss) : false;
  const accent = SEVERITY_COLOR[severity];

  return (
    <div
      role={narration.role}
      aria-live={narration.ariaLive}
      aria-atomic="true"
      data-testid={testId}
      data-severity={severity}
      className={className}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: tokens.space[2],
        padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
        background: tokens.color.surface[2],
        color: tokens.color.ink.primary,
        borderLeft: `${tokens.space[0]}px solid ${accent}`,
        borderLeftWidth: 4,
        borderRadius: tokens.radius.md,
        boxShadow: tokens.elevation[1],
      }}
    >
      <Icon name={icon ?? SEVERITY_ICON[severity]} size={20} decorative />
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <div
          style={{
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.body.size,
            fontWeight: tokens.font.weight.medium,
            lineHeight: tokens.font.role.body.lineHeight,
          }}
        >
          {title}
        </div>
        {body ? (
          <div
            style={{
              marginTop: tokens.space[1],
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.body.size,
              lineHeight: tokens.font.role.body.lineHeight,
              color: tokens.color.ink.secondary,
            }}
          >
            {body}
          </div>
        ) : null}
        {errorCode ? (
          <div
            style={{
              marginTop: tokens.space[1],
              fontFamily: tokens.font.family.mono,
              fontSize: tokens.font.role.engMeta.size,
              color: tokens.color.ink.muted,
            }}
          >
            {errorCode}
          </div>
        ) : null}
        {actions && actions.length > 0 ? (
          <div
            style={{
              marginTop: tokens.space[2],
              display: 'flex',
              gap: tokens.space[2],
            }}
          >
            {actions.map((a, idx) => (
              <button
                key={`${a.label}-${idx}`}
                type="button"
                onClick={a.onClick}
                style={{
                  appearance: 'none',
                  background: idx === 0 ? accent : 'transparent',
                  color: idx === 0 ? tokens.color.accent.on : tokens.color.ink.primary,
                  border: idx === 0
                    ? '1px solid transparent'
                    : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                  borderRadius: tokens.radius.input,
                  padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.role.body.size,
                  cursor: 'pointer',
                  minHeight: tokens.density.touchMin,
                }}
              >
                {a.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {dismissible ? (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          style={{
            appearance: 'none',
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            color: tokens.color.ink.secondary,
            width: tokens.density.touchMin,
            height: tokens.density.touchMin,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: tokens.radius.sm,
          }}
        >
          <Icon name="close" size={20} decorative />
        </button>
      ) : null}
    </div>
  );
}
